/**
 * Focus Trap Mixin for Modal Components
 *
 * Implements WCAG 2.1.2 No Keyboard Trap and 2.4.3 Focus Order requirements:
 * - Moves focus to modal when opened (optional, controlled by focusTrapAutoFocus)
 * - Traps focus within modal (Tab/Shift+Tab cycle)
 * - Closes modal on ESC key press
 * - Restores focus to trigger element when closed
 *
 * Usage:
 * 1. Import and add to mixins array
 * 2. Set `focusTrapEnabled` data property to control when trap is active
 * 3. Ensure modal container has ref="focusTrapContainer"
 * 4. Optionally implement `onFocusTrapEscape()` method for custom close behavior
 * 5. Optionally set `focusTrapAutoFocus: false` to prevent auto-focus on activation
 * 6. Optionally mark an element inside the container `data-autofocus` to receive
 *    focus instead of the first focusable element; it may be kept out of the Tab
 *    order with `tabindex="-1"`, like a heading. It is centered on activation
 *    whether or not it can take focus, so a disabled element can still mark the
 *    position the container should open at
 * 7. Optionally define `focusTrapRestoresFocus` (prop, data or computed) as false
 *    while the trap only pauses: the focus stays where the content put it.
 * 8. Optionally define `focusTrapReturnSelector` (prop, data or computed): when
 *    the opener left the DOM, before or while the trap held the focus, the
 *    first visible element it matches gets the focus back instead.
 * 9. While active, the trap answers back the way it answers Escape. Set
 *    `focusTrapStepsBack: false` when the component answers back itself, or
 *    when its Escape may close nothing: a step that stays on top would leave
 *    back in the app with no way out of the reader.
 *
 * @example
 * import focusTrapMixin from '_shared_/js/mixins/focusTrapMixin';
 *
 * export default {
 *     mixins: [focusTrapMixin],
 *     data() {
 *         return {
 *             focusTrapEnabled: false,
 *             // Optional: set to false to prevent auto-focus when trap activates
 *             // focusTrapAutoFocus: false,
 *         };
 *     },
 *     methods: {
 *         openModal() {
 *             this.focusTrapEnabled = true;
 *         },
 *         closeModal() {
 *             this.focusTrapEnabled = false;
 *         },
 *         // Optional: custom close behavior for ESC key
 *         onFocusTrapEscape() {
 *             this.closeModal();
 *         },
 *     },
 * };
 */

import { pushBackStep } from '../utils/backSteps';

const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(', ');

/**
 * Opt-in marker letting content declare where focus should land when the trap
 * activates, instead of the first focusable element. Content owns the marker, so
 * it works across the slot boundary of components that only host the panel.
 */
const INITIAL_FOCUS_SELECTOR = '[data-autofocus]';

export default {
    data() {
        return {
            _focusTrap_previousActiveElement: null,
        };
    },
    watch: {
        focusTrapEnabled: {
            immediate: true,
            handler(isEnabled) {
                if (isEnabled) {
                    this._focusTrap_activate();
                } else {
                    this._focusTrap_deactivate(this.focusTrapRestoresFocus !== false);
                }
            },
        },
    },
    beforeDestroy() {
        // Even mid-pause, teardown hands the focus back: no later close will.
        this._focusTrap_deactivate();
    },
    methods: {
        /**
         * Activates the focus trap
         */
        _focusTrap_activate() {
            // Kept across a pause, so a later close still returns to the original opener.
            if (!this._focusTrap_previousActiveElement) this._focusTrap_previousActiveElement = document.activeElement;

            // Wait for DOM to update, then set up the trap
            this.$nextTick(() => {
                // Turned off or torn down before this tick: deactivate already ran, so
                // a listener added now would never be removed and would keep
                // swallowing Escape for the rest of the session.
                if (!this.focusTrapEnabled || this._isDestroyed) return;

                // Only auto-focus if focusTrapAutoFocus is not explicitly false
                if (this.focusTrapAutoFocus !== false) {
                    this._focusTrap_focusFirstElement();
                }
                document.addEventListener('keydown', this._focusTrap_handleKeyDown);
                if (this.focusTrapStepsBack !== false) {
                    this._focusTrap_leaveBackSteps = pushBackStep(this._focusTrap_handleEscape);
                }
            });
        },

        /**
         * Deactivates the focus trap and restores focus
         */
        _focusTrap_deactivate(restoresFocus = true) {
            document.removeEventListener('keydown', this._focusTrap_handleKeyDown);
            this._focusTrap_leaveBackSteps?.();
            this._focusTrap_leaveBackSteps = null;

            // A pausing trap leaves the focus where the content put it.
            if (!restoresFocus) return;

            // Restore focus to the element that opened the modal
            if (this._focusTrap_previousActiveElement && this._focusTrap_previousActiveElement.focus) {
                this.$nextTick(() => {
                    // The body stands for an opener that was gone before the trap opened.
                    const opener = this._focusTrap_previousActiveElement;
                    const openerIsThere = opener.isConnected && opener !== document.body;
                    (openerIsThere ? opener : this._focusTrap_returnTarget())?.focus();
                    this._focusTrap_previousActiveElement = null;
                });
            }
        },

        /**
         * Where the focus goes back when the opener is gone, e.g. a header
         * button the open panel took off screen and that rendered again.
         * @returns {HTMLElement|null}
         */
        _focusTrap_returnTarget() {
            if (!this.focusTrapReturnSelector) return null;

            return (
                [...document.querySelectorAll(this.focusTrapReturnSelector)].find(
                    element => element.getClientRects().length > 0
                ) ?? null
            );
        },

        /**
         * Gets the modal container element
         * @returns {HTMLElement|null}
         */
        _focusTrap_getContainer() {
            return this.$refs.focusTrapContainer || null;
        },

        /**
         * Gets all focusable elements within the modal
         * @returns {HTMLElement[]}
         */
        _focusTrap_getFocusableElements() {
            const container = this._focusTrap_getContainer();
            if (!container) return [];

            const elements = container.querySelectorAll(FOCUSABLE_SELECTORS);
            return Array.from(elements).filter(el => {
                // Filter out elements that are not visible
                return el.offsetParent !== null && !el.hasAttribute('inert');
            });
        },

        /**
         * Focuses where the trap should open: the element the content marked
         * `data-autofocus`, if it takes focus, otherwise the first focusable
         * element. Panels can opt into focusing the container when content has
         * no focus target. A marked element is centered whether it can take focus.
         */
        _focusTrap_focusFirstElement() {
            const container = this._focusTrap_getContainer();
            if (!container) return;

            const focusableElements = this._focusTrap_getFocusableElements();
            const declaredTarget = container.querySelector(INITIAL_FOCUS_SELECTOR);
            let focusedDeclaredTarget = false;

            if (declaredTarget) {
                declaredTarget.focus();
                // Confirm focus actually landed rather than filtering first: the target
                // may sit outside the Tab order on purpose, like a heading, and `inert`
                // and `disabled` are inherited, so its own attributes cannot say whether
                // the browser will refuse it.
                focusedDeclaredTarget = document.activeElement === declaredTarget;
            }

            if (!focusedDeclaredTarget) {
                if (this.focusTrapFallback === 'container') {
                    container.setAttribute('tabindex', '-1');
                    container.focus({ preventScroll: true });
                } else if (focusableElements.length > 0) {
                    focusableElements[0].focus();
                } else {
                    // If no focusable elements, focus the container itself
                    container.setAttribute('tabindex', '-1');
                    container.focus();
                }
            }

            // Centered last so the fallback's own scroll cannot undo it. Marking a
            // position and being able to take focus are separate: an entry that is
            // disabled — a table of contents row in preview mode — still says where
            // the panel should open, so it gets centered without holding focus.
            declaredTarget?.scrollIntoView?.({ block: 'center' });
        },

        /**
         * Handles keydown events for focus trapping and ESC to close
         * @param {KeyboardEvent} event
         */
        _focusTrap_handleKeyDown(event) {
            // Handle ESC key to close modal
            if (event.key === 'Escape') {
                event.preventDefault();
                this._focusTrap_handleEscape();
                return;
            }

            // Handle Tab key for focus cycling
            if (event.key !== 'Tab') return;

            const focusableElements = this._focusTrap_getFocusableElements();
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const container = this._focusTrap_getContainer();

            if (document.activeElement === container) {
                event.preventDefault();
                (event.shiftKey ? lastElement : firstElement).focus();
                return;
            }

            if (event.shiftKey) {
                // Shift + Tab: going backwards
                if (document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab: going forwards
                if (document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        },

        /**
         * Handles ESC key press to close the modal
         * Components can override onFocusTrapEscape() for custom close behavior
         */
        _focusTrap_handleEscape() {
            if (typeof this.onFocusTrapEscape === 'function') {
                this.onFocusTrapEscape();
            } else {
                this.focusTrapEnabled = false;
            }
        },
    },
};
